import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@quebon/ui/components/dialog'
import { useRegisterBillingKey } from '@/hooks/admin/useContentFee'
import type { ChangeEvent, FormEvent } from 'react'

interface Props {
  open: boolean
  onClose: () => void
}

interface FormState {
  cardNumber1: string
  cardNumber2: string
  cardNumber3: string
  cardNumber4: string
  cardExpirationMonth: string
  cardExpirationYear: string
  customerIdentityNumber: string
  cardPassword: string
  cardAlias: string
}

const emptyForm: FormState = {
  cardNumber1: '',
  cardNumber2: '',
  cardNumber3: '',
  cardNumber4: '',
  cardExpirationMonth: '',
  cardExpirationYear: '',
  customerIdentityNumber: '',
  cardPassword: '',
  cardAlias: '',
}

const cardNumberFields: Array<keyof Pick<FormState, 'cardNumber1' | 'cardNumber2' | 'cardNumber3' | 'cardNumber4'>> = [
  'cardNumber1',
  'cardNumber2',
  'cardNumber3',
  'cardNumber4',
]

const digitsOnly = (value: string, maxLength: number) => value.replace(/\D/g, '').slice(0, maxLength)

export function BillingKeyRegistrationDialog({ open, onClose }: Props) {
  const { t } = useTranslation(['admin', 'common'])
  const [form, setForm] = useState<FormState>(emptyForm)
  const mutation = useRegisterBillingKey()
  const cardNumber = cardNumberFields.map((field) => form[field]).join('')

  useEffect(() => {
    if (!open) setForm(emptyForm)
  }, [open])

  const updateDigitsField = (field: keyof FormState, maxLength: number) => (event: ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: digitsOnly(event.target.value, maxLength) }))
  }

  const updateField = (field: keyof FormState) => (event: ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }))
  }

  const handleClose = () => {
    setForm(emptyForm)
    onClose()
  }

  const canSubmit = Boolean(
    cardNumber.length === 16 &&
    form.cardExpirationMonth.length === 2 &&
    form.cardExpirationYear.length === 2 &&
    (form.customerIdentityNumber.length === 6 || form.customerIdentityNumber.length === 10) &&
    form.cardPassword.length === 2 &&
    !mutation.isPending,
  )

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canSubmit) return
    mutation.mutate(
      {
        cardNumber,
        cardExpirationMonth: form.cardExpirationMonth,
        cardExpirationYear: form.cardExpirationYear,
        customerIdentityNumber: form.customerIdentityNumber,
        cardPassword: form.cardPassword,
        cardAlias: form.cardAlias.trim() ? form.cardAlias.trim() : null,
      },
      { onSuccess: handleClose },
    )
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) handleClose() }}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{t('admin:students.detail.contentFee.billingKeys.registration.title')}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <div className="text-sm font-medium text-gray-700">
                {t('admin:students.detail.contentFee.billingKeys.registration.cardNumber')}
              </div>
              <div className="mt-1 grid grid-cols-4 gap-2">
                {cardNumberFields.map((field, index) => (
                  <input
                    key={field}
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? 'cc-number' : 'off'}
                    maxLength={4}
                    aria-label={`${t('admin:students.detail.contentFee.billingKeys.registration.cardNumber')} ${index + 1}`}
                    placeholder="0000"
                    value={form[field]}
                    onChange={updateDigitsField(field, 4)}
                    className="h-11 w-full rounded-lg border border-gray-300 px-2 text-center font-mono text-sm tracking-widest focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {t('admin:students.detail.contentFee.billingKeys.registration.cardNumberHint')}
              </p>
            </div>
            <label className="block text-sm font-medium text-gray-700">
              {t('admin:students.detail.contentFee.billingKeys.registration.expiryMonth')}
              <input
                type="text"
                inputMode="numeric"
                autoComplete="cc-exp-month"
                maxLength={2}
                value={form.cardExpirationMonth}
                onChange={updateDigitsField('cardExpirationMonth', 2)}
                placeholder="MM"
                className="mt-1 h-11 w-full rounded-lg border border-gray-300 px-3 py-2 text-center font-mono tracking-widest focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              {t('admin:students.detail.contentFee.billingKeys.registration.expiryYear')}
              <input
                type="text"
                inputMode="numeric"
                autoComplete="cc-exp-year"
                maxLength={2}
                value={form.cardExpirationYear}
                onChange={updateDigitsField('cardExpirationYear', 2)}
                placeholder="YY"
                className="mt-1 h-11 w-full rounded-lg border border-gray-300 px-3 py-2 text-center font-mono tracking-widest focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </label>
            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="billing-key-identity-number" className="block text-sm font-medium text-gray-700">
                {t('admin:students.detail.contentFee.billingKeys.registration.identityNumber')}
              </label>
              <input
                id="billing-key-identity-number"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={10}
                aria-describedby="billing-key-identity-number-hint"
                value={form.customerIdentityNumber}
                onChange={updateDigitsField('customerIdentityNumber', 10)}
                placeholder={t('admin:students.detail.contentFee.billingKeys.registration.identityNumberPlaceholder')}
                className="mt-1 h-11 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p id="billing-key-identity-number-hint" className="mt-1 text-xs font-normal text-gray-500">
                {t('admin:students.detail.contentFee.billingKeys.registration.identityNumberHint')}
              </p>
            </div>
            <label className="block text-sm font-medium text-gray-700">
              {t('admin:students.detail.contentFee.billingKeys.registration.cardPassword')}
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={2}
                value={form.cardPassword}
                onChange={updateDigitsField('cardPassword', 2)}
                placeholder="**"
                className="mt-1 h-11 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </label>
            <label className="col-span-2 block text-sm font-medium text-gray-700">
              {t('admin:students.detail.contentFee.billingKeys.registration.alias')}
              <input
                type="text"
                value={form.cardAlias}
                onChange={updateField('cardAlias')}
                className="mt-1 h-11 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </label>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200"
            >
              {t('common:button.cancel')}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-lg bg-primary-600 px-4 py-2 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {mutation.isPending
                ? t('admin:students.detail.contentFee.billingKeys.registration.submitting')
                : t('admin:students.detail.contentFee.billingKeys.registration.submit')}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
